import { useState, useCallback, useEffect } from 'react';
import { 
  Container, 
  CssBaseline, 
  ThemeProvider, 
  createTheme,
  IconButton,
  Box,
  useMediaQuery
} from '@mui/material';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import { Toaster } from 'react-hot-toast';
import Header from './components/Header';
import QRGenerator from './components/QRGenerator';
import Footer from './components/Footer';
import AdComponent from './components/AdComponent';

function App() {
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const [mode, setMode] = useState(prefersDarkMode ? 'dark' : 'light');
  const [userCount, setUserCount] = useState(1000);

  const theme = createTheme({
    palette: {
      mode,
      primary: {
        main: '#6366f1',
      },
      background: {
        default: mode === 'light' ? '#f9fafb' : '#121212',
        paper: mode === 'light' ? '#ffffff' : '#1e1e1e',
      },
    },
    shape: {
      borderRadius: 12,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
          },
        },
      },
    },
  });

  // Increment user count randomly
  const incrementUserCount = useCallback(() => {
    setUserCount(prev => prev + Math.floor(Math.random() * 3));
  }, []);

  // Update count every 30 seconds
  useEffect(() => {
    const interval = setInterval(incrementUserCount, 30000);
    return () => clearInterval(interval);
  }, [incrementUserCount]);

  const toggleColorMode = () => {
    setMode(prevMode => prevMode === 'light' ? 'dark' : 'light');
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Toaster 
        position="top-center"
        toastOptions={{
          style: {
            background: theme.palette.background.paper,
            color: theme.palette.text.primary,
          },
        }}
      />
      <Box 
        sx={{
          position: 'fixed',
          top: 16,
          right: 16,
          zIndex: 1000,
        }}
      >
        <IconButton onClick={toggleColorMode} color="inherit">
          {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
        </IconButton>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'flex-start',
          width: '100%',
          minHeight: '100vh',
          bgcolor: 'background.default',
        }}
      >
        {/* Left Ad - only on large screens */}
        <Box
          sx={{
            width: 120,
            minWidth: 120,
            display: { xs: 'none', lg: 'flex' },
            justifyContent: 'center',
            alignItems: 'flex-start',
            position: 'sticky',
            top: 32,
            height: '100vh',
            zIndex: 10,
          }}
        >
          <AdComponent slot="left" />
        </Box>
        {/* Main Content */}
        <Container
          maxWidth="md"
          sx={{
            py: 4,
            flex: 1,
            minWidth: 0,
            zIndex: 1,
          }}
        >
          <Header />
          <QRGenerator />
          <Footer userCount={userCount} />
        </Container>
        {/* Right Ad - only on large screens */}
        <Box
          sx={{
            width: 120,
            minWidth: 120,
            display: { xs: 'none', lg: 'flex' },
            justifyContent: 'center',
            alignItems: 'flex-start',
            position: 'sticky',
            top: 32,
            height: '100vh',
            zIndex: 10,
          }}
        >
          <AdComponent slot="right" />
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default App;
